// Plan 08-02 — React context provider for auth state.
//
// Exposes:
//   - kind: 'unknown' | 'guest' | 'authenticated' (state machine)
//   - loginWithApple()  — interactive popup → exchange → route /lobby
//   - loginWithGoogle() — interactive popup → exchange → route /lobby
//   - logout()          — clearAuth + broadcast + route /splash
//
// On mount:
//   1. Call silentRefreshIfNeeded() best-effort. On any throw or false
//      result, leave the atom value untouched and let the existing bearer
//      (if any) drive the state machine.
//   2. Subscribe to BroadcastChannel('runegambit-auth') so a sibling tab's
//      logout/refresh updates this tab's perceived state immediately.
//   3. Subscribe to $authBearer; the React context value is derived from it.
//
// State machine:
//   - 'unknown'         → first paint, before silent-refresh resolves
//   - 'guest'           → no bearer
//   - 'authenticated'   → bearer present and not expired

import { useStore } from '@nanostores/react'
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { navigateTo } from '../router/nav.js'
import { $authBearer, clearAuth } from '../stores/auth.js'
import { consumeReturnTo } from '../stores/returnTo.js'
import { signInWithApple } from './apple.js'
import { exchangeGoogleCode, exchangeIdpToken } from './exchange.js'
import { signInWithGoogleCode } from './google.js'
import {
  REFRESH_CHANNEL_NAME,
  silentRefreshIfNeeded,
} from './silent-refresh.js'

export type AuthState =
  | { kind: 'unknown' }
  | { kind: 'guest' }
  | { kind: 'authenticated'; user_uuid: string; jwt: string }

interface AuthActions {
  loginWithApple: () => Promise<void>
  loginWithGoogle: () => Promise<void>
  logout: () => void
}

export type AuthContextValue = AuthState & AuthActions

const AuthContext = createContext<AuthContextValue | null>(null)

export interface AuthProviderProps {
  children: ReactNode
  /** Optional injection point for routing — Plan 08-02 Task 3 wires the
   *  router; until then we accept a no-op default. */
  onNavigate?: (path: string) => void
}

export function AuthProvider({
  children,
  onNavigate = navigateTo,
}: AuthProviderProps): JSX.Element {
  const bearer = useStore($authBearer)
  const [bootstrapping, setBootstrapping] = useState(true)

  // Mount-time silent refresh + cross-tab broadcast subscription.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await silentRefreshIfNeeded()
      } catch {
        // Swallow — caller drops to guest below if no bearer survived.
      }
      if (!cancelled) setBootstrapping(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel(REFRESH_CHANNEL_NAME)
    } catch {
      return
    }
    const onMessage = (event: MessageEvent): void => {
      const data = event.data as { kind?: string } | null
      if (data?.kind === 'logout') {
        clearAuth()
        onNavigate?.('/splash')
      }
      // 'refreshed' is a no-op here — nanostores's persistent atom listens
      // to the storage event already and re-reads the new value.
    }
    ch.addEventListener('message', onMessage)
    return () => {
      ch?.removeEventListener('message', onMessage)
      ch?.close()
    }
  }, [onNavigate])

  const loginWithApple = useCallback(async () => {
    // Plan 09-11 / CR-01: ONE nonce, threaded through Apple init AND the
    // body. Apple's strict path (nonce_supported=true) bakes this verbatim
    // into the JWT; backend (jwks.ts) compares JWT nonce === body nonce.
    // Pre-fix this generated TWO randomUUIDs (one in apple.ts, a separate
    // one here) → 401 every time on the strict path.
    const nonce = crypto.randomUUID()
    const idToken = await signInWithApple({ nonce })
    await exchangeIdpToken({
      idp_kind: 'apple',
      id_token: idToken,
      nonce,
    })
    // ARCA-93 / WS2: resume the route a guest was redirected from (default
    // /lobby). consumeReturnTo reads-and-clears the WS1 guard's stash.
    onNavigate?.(consumeReturnTo())
  }, [onNavigate])

  const loginWithGoogle = useCallback(async () => {
    // OAuth 2.0 auth-code popup flow (replaces the prior One Tap/id_token
    // path for initial sign-in). The popup opens Google's full account
    // picker — works whether or not the user already has a Google session.
    // The code is exchanged server-side for an id_token using
    // GOOGLE_CLIENT_SECRET; the browser never sees the id_token.
    //
    // Replay protection: Google enforces single-use on the code, postMessage
    // binds the popup → parent origin, and the server-side client_secret
    // gates the token exchange. No PKCE because initCodeClient does not
    // expose it and the documented flow does not require it.
    //
    // Silent refresh (silent-refresh.ts) continues to use One Tap +
    // id_token; the two flows are independent.
    const code = await new Promise<string>((resolve, reject) => {
      signInWithGoogleCode({ onCode: resolve, onError: reject }).catch(reject)
    })
    await exchangeGoogleCode({
      code,
      redirect_uri: 'postmessage',
    })
    // ARCA-93 / WS2: resume the route a guest was redirected from (default
    // /lobby). consumeReturnTo reads-and-clears the WS1 guard's stash.
    onNavigate?.(consumeReturnTo())
  }, [onNavigate])

  const logout = useCallback(() => {
    clearAuth()
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const ch = new BroadcastChannel(REFRESH_CHANNEL_NAME)
        ch.postMessage({ kind: 'logout' })
        ch.close()
      } catch {
        // Best-effort.
      }
    }
    onNavigate?.('/splash')
  }, [onNavigate])

  const value = useMemo<AuthContextValue>(() => {
    let state: AuthState
    if (bootstrapping) {
      state = { kind: 'unknown' }
    } else if (!bearer || bearer.exp_ms <= Date.now()) {
      state = { kind: 'guest' }
    } else {
      state = {
        kind: 'authenticated',
        user_uuid: bearer.user_uuid,
        jwt: bearer.jwt,
      }
    }
    return { ...state, loginWithApple, loginWithGoogle, logout }
  }, [bearer, bootstrapping, loginWithApple, loginWithGoogle, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider />')
  }
  return ctx
}
